import { z } from 'zod';

const chatApiRequestBackendSchema = z.object({
  query: z.string().min(1, 'Query is required'),
  role: z.string().min(1, 'Role is required'),
});

const chatInitiateResponseBackendSchema = z.object({
  status_code: z.literal(202),
  task_id: z.string().min(1, 'task_id is required'),
  response: z.literal('Request Accepted and it is in process'),
});

const contentBlockMediaBackendSchema = z.object({
  url: z.string(),
  s3_uri: z.string().nullish(),
});

const contentBlockBackendSchema = z.object({
  type: z.string(),
  text: z.string().nullish(),
  step_number: z.number().nullish(),
  citations: z.array(z.string()).nullish(),
  media: z.array(contentBlockMediaBackendSchema).nullish(),
});

const chatSourceBackendSchema = z.object({
  text: z.string().nullish(),
  page_number: z.number().nullish(),
  source_filename: z.string(),
  image_presigned_urls: z.array(z.string()).nullish(),
});

type ContentBlockBackend = z.infer<typeof contentBlockBackendSchema>;
type ChatSourceBackend = z.infer<typeof chatSourceBackendSchema>;

const mapContentBlock = (block: ContentBlockBackend) => ({
  type: block.type,
  text: block.text ?? undefined,
  stepNumber: block.step_number ?? undefined,
  citations: block.citations ?? undefined,
  media: block.media?.map((item) => ({
    url: item.url,
    s3Uri: item.s3_uri ?? undefined,
  })),
});

const mapChatSource = (source: ChatSourceBackend) => ({
  text: source.text ?? undefined,
  pageNumber: source.page_number ?? undefined,
  sourceFilename: source.source_filename,
  imagePresignedUrls: source.image_presigned_urls ?? undefined,
});

const chatFinalResponseBackendSchema = z.object({
  status_code: z.number(),
  task_id: z.string(),
  response: z.string(),
  report: z.boolean().optional(),
  file_link: z.array(z.string()).optional(),
  api_explanation: z.record(z.string(), z.unknown()).optional(),
  content_blocks: z.array(contentBlockBackendSchema).optional(),
  sources: z.array(chatSourceBackendSchema).optional(),
});

const chatReactionCreatePayloadBackendSchema = z.object({
  message: z.string().min(1, 'Message is required'),
  metadata: z.object({
    timestamp: z.string(),
    sender_id: z.number().int(),
  }),
  reaction: z.enum(['liked', 'disliked']),
  favourite: z.boolean(),
  category: z.enum(['meds', 'stats', 'staff']).optional(),
});

const chatReactionUpdatePayloadBackendSchema = z.object({
  message_id: z.number().int().positive(),
  reaction: z.enum(['liked', 'disliked']),
  favourite: z.boolean(),
});

export const chatInitiateResponseSchema =
  chatInitiateResponseBackendSchema.transform((data) => ({
    statusCode: data.status_code,
    taskId: data.task_id,
    response: data.response,
  }));

export const chatFinalResponseSchema = chatFinalResponseBackendSchema.transform(
  (data) => ({
    statusCode: data.status_code,
    taskId: data.task_id,
    response: data.response,
    report: data.report,
    fileLink: data.file_link,
    apiExplanation: data.api_explanation,
    contentBlocks: data.content_blocks?.map(mapContentBlock),
    sources: data.sources?.map(mapChatSource),
  }),
);

export const chatApiRequestSchema = chatApiRequestBackendSchema;
export const chatReactionCreatePayloadSchema =
  chatReactionCreatePayloadBackendSchema;
export const chatReactionUpdatePayloadSchema =
  chatReactionUpdatePayloadBackendSchema;

export const messageIdGenerationSchema = z.object({
  content: z.string().min(1, 'Content must be a non-empty string'),
  senderId: z.number().refine((val) => !Number.isNaN(val), {
    message: 'senderId must be a valid number',
  }),
});

export const taskIdSchema = z
  .string()
  .min(1, 'taskId is required and must be a non-empty string');

export const reactionParamsSchema = z.object({
  customerId: z.union([z.string(), z.number()]).transform((val) => {
    const num = Number(val);
    if (Number.isNaN(num)) {
      throw new Error('customerId must be a number or numeric string');
    }
    return num;
  }),
  message: z.object({
    content: z.string().min(1, 'Message content is required'),
  }),
  reaction: z.enum(['liked', 'disliked']),
  favourite: z.boolean(),
});

export type ChatApiRequestBackend = z.infer<typeof chatApiRequestBackendSchema>;
export type ChatInitiateResponse = z.infer<typeof chatInitiateResponseSchema>;
export type ChatFinalResponse = z.infer<typeof chatFinalResponseSchema>;
export type ChatContentBlock = NonNullable<
  ChatFinalResponse['contentBlocks']
>[number];
export type ChatSource = NonNullable<ChatFinalResponse['sources']>[number];
export type ChatReactionCreatePayload = z.infer<
  typeof chatReactionCreatePayloadBackendSchema
>;

const chatHistoryMessageBackendSchema = z.object({
  role: z.string(),
  content: z
    .union([z.string(), z.number()])
    .transform((value) => String(value)),
  file_link: z.array(z.string()).nullish(),
  report: z.boolean().nullish(),
  time: z.string().nullish(),
  content_blocks: z.array(contentBlockBackendSchema).nullish(),
  sources: z.array(chatSourceBackendSchema).nullish(),
});

export type ChatHistoryMessage = {
  role: 'user' | 'assistant';
  content: string;
  fileLink?: string[];
  report?: boolean;
  time?: string;
  contentBlocks?: ChatContentBlock[];
  sources?: ChatSource[];
};

const normalizeHistoryRole = (role: string): 'user' | 'assistant' =>
  role.trim().toLowerCase() === 'user' ? 'user' : 'assistant';

const mapChatHistoryMessage = (
  msg: z.infer<typeof chatHistoryMessageBackendSchema>,
): ChatHistoryMessage => ({
  role: normalizeHistoryRole(msg.role),
  content: msg.content,
  fileLink: msg.file_link ?? undefined,
  report: msg.report ?? undefined,
  time: msg.time ?? undefined,
  contentBlocks: msg.content_blocks?.map(mapContentBlock),
  sources: msg.sources?.map(mapChatSource),
});

export const chatHistoryApiResponseSchema = z.object({
  customer_id: z.number(),
  history: z.array(chatHistoryMessageBackendSchema),
  presigned_url: z.string().optional(),
});

export const chatHistoryPresignedFileSchema = z.union([
  z.object({
    customer_id: z.number().optional(),
    history: z.array(chatHistoryMessageBackendSchema),
  }),
  z.array(chatHistoryMessageBackendSchema),
]);

export const chatHistoryPresignedItemSchema = chatHistoryMessageBackendSchema;

export const parseChatHistoryMessages = (
  messages: z.infer<typeof chatHistoryMessageBackendSchema>[],
): ChatHistoryMessage[] => messages.map(mapChatHistoryMessage);

/** @deprecated Use parseChatHistoryMessages with chatHistoryApiResponseSchema */
export const chatHistoryResponseSchema = chatHistoryApiResponseSchema.transform(
  (data) => parseChatHistoryMessages(data.history),
);
export type ChatReactionUpdatePayload = z.infer<
  typeof chatReactionUpdatePayloadBackendSchema
>;

const chatReactionRecordBackendSchema = z.object({
  message_id: z.number().int(),
  customer_id: z.number().int(),
  message: z.string(),
  reaction: z.enum(['liked', 'disliked']),
  favourite: z.boolean(),
  category: z.string().nullable().optional(),
  meta_data: z.record(z.string(), z.unknown()).nullable().optional(),
  created_at: z.string().optional(),
  last_updated_at: z.string().optional(),
});

export const chatReactionsResponseSchema = z.array(
  chatReactionRecordBackendSchema,
);

export type ChatReactionRecord = z.infer<
  typeof chatReactionRecordBackendSchema
>;
